/**
 * Authenticated application frame (spec §22.2/§22.3).
 *
 * Desktop: permanent left sidebar with permission-filtered navigation and a
 * user menu (locale switcher + logout). Mobile: top bar plus bottom
 * navigation with a prominent central "New request" action; everything else
 * lives in the "More" sheet.
 */
import { useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import type { Permission } from "@offertli/shared";
import { apiPost } from "../../lib/api.js";
import { useLogout, useMe, usePermissions } from "../../lib/auth.js";
import { useAppConfig } from "../../lib/app-config.js";
import { LocaleSync } from "../guards.js";
import { Avatar } from "@primereact/ui/avatar";
import { Button } from "../ui/Button.js";
import { Dialog as Modal } from "@primereact/ui/dialog";
import { Popover } from "@primereact/ui/popover";
import { cx } from "../ui/cx.js";
import { ThemeSelector } from "../ThemeSelector.js";
interface NavItem {
  to: string;
  labelKey: string;
  icon: string;
  /** Visible when every allOf and at least one anyOf permission is granted. */
  anyOf: Permission[];
  allOf?: Permission[];
  end?: boolean;
  children?: NavItem[];
}
const NAV_ITEMS: NavItem[] = [
  {
    to: "/",
    labelKey: "nav.dashboard",
    icon: "pi pi-home",
    anyOf: [],
    end: true,
  },
  {
    to: "/requests",
    labelKey: "nav.requests",
    icon: "pi pi-inbox",
    anyOf: ["requests.view_own", "requests.view_all"],
  },
  {
    to: "/customers",
    labelKey: "nav.customers",
    icon: "pi pi-users",
    anyOf: ["customers.view"],
  },
  {
    to: "/offers",
    labelKey: "nav.offers",
    icon: "pi pi-file",
    anyOf: ["offers.view"],
    allOf: ["finance.view"],
  },
  {
    to: "/projects",
    labelKey: "nav.projects",
    icon: "pi pi-briefcase",
    anyOf: ["projects.view_assigned", "projects.view_all"],
  },
  {
    to: "/planner",
    labelKey: "nav.planner",
    icon: "pi pi-calendar",
    anyOf: ["projects.manage_planning_all", "projects.view_all"],
  },
  {
    to: "/invoices",
    labelKey: "nav.invoices",
    icon: "pi pi-wallet",
    anyOf: ["invoices.view"],
    allOf: ["finance.view"],
  },
  {
    to: "/catalog",
    labelKey: "nav.catalog",
    icon: "pi pi-tags",
    anyOf: ["offers.view", "inventory.view"],
  },
  {
    to: "/time",
    labelKey: "nav.time",
    icon: "pi pi-clock",
    anyOf: ["time.record", "time.approve"],
    children: [
      {
        to: "/time/clock",
        labelKey: "nav.clock",
        icon: "pi pi-stopwatch",
        anyOf: ["time.record"],
      },
      {
        to: "/time/week",
        labelKey: "nav.week",
        icon: "pi pi-calendar",
        anyOf: ["time.record"],
      },
      {
        to: "/time/assignments",
        labelKey: "nav.assignments",
        icon: "pi pi-calendar-clock",
        anyOf: ["time.record"],
      },
      {
        to: "/time/approvals",
        labelKey: "nav.approvals",
        icon: "pi pi-check-square",
        anyOf: ["time.approve"],
      },
    ],
  },
  {
    to: "/expenses",
    labelKey: "nav.expenses",
    icon: "pi pi-receipt",
    anyOf: [
      "expenses.submit",
      "expenses.view_own",
      "expenses.view_all",
      "expenses.pay",
    ],
  },
  {
    to: "/employees",
    labelKey: "nav.employees",
    icon: "pi pi-id-card",
    anyOf: ["employees.view_self", "employees.view_all"],
  },
  {
    to: "/notifications",
    labelKey: "nav.notifications",
    icon: "pi pi-bell",
    anyOf: [],
  },
  {
    to: "/settings",
    labelKey: "nav.settings",
    icon: "pi pi-cog",
    anyOf: [],
    children: [
      {
        to: "/organization",
        labelKey: "nav.organization",
        icon: "pi pi-sitemap",
        anyOf: [],
      },
      {
        to: "/settings?tab=company",
        labelKey: "settings.company",
        icon: "pi pi-building",
        anyOf: ["settings.view"],
      },
      {
        to: "/settings?tab=billing",
        labelKey: "settings.billing",
        icon: "pi pi-wallet",
        anyOf: ["finance.view"],
      },
      {
        to: "/settings?tab=documents",
        labelKey: "settings.documents",
        icon: "pi pi-file",
        anyOf: ["settings.view"],
      },
      {
        to: "/settings?tab=localization",
        labelKey: "settings.localization",
        icon: "pi pi-globe",
        anyOf: ["settings.view"],
      },
      {
        to: "/settings?tab=categories",
        labelKey: "settings.categories",
        icon: "pi pi-sitemap",
        anyOf: ["settings.view"],
      },
      {
        to: "/settings?tab=holidays",
        labelKey: "settings.holidays",
        icon: "pi pi-calendar",
        anyOf: ["time.manage"],
      },
      {
        to: "/settings?tab=roles",
        labelKey: "settings.roles",
        icon: "pi pi-shield",
        anyOf: ["roles.view"],
      },
      {
        to: "/settings?tab=automations",
        labelKey: "automations.webhooks",
        icon: "pi pi-bolt",
        anyOf: ["automations.view"],
      },
      {
        to: "/settings?tab=ai",
        labelKey: "settings.aiTab",
        icon: "pi pi-sparkles",
        anyOf: ["settings.view"],
        allOf: ["settings.system.edit"],
      },
      {
        to: "/settings?tab=system",
        labelKey: "settings.system",
        icon: "pi pi-sliders-h",
        anyOf: ["settings.system.edit"],
      },
      {
        to: "/settings?tab=systemAdmin",
        labelKey: "settings.systemAdmin",
        icon: "pi pi-lock",
        anyOf: ["settings.system.edit"],
      },
    ],
  },
];
function Logo({ compact = false }: { compact?: boolean }) {
  const { branding } = useAppConfig();
  return (
    <>
      <Link to="/" className="flex items-center gap-2.5 rounded-lg">
        <img
          src={branding.logoUrl ?? "/favicon.svg"}
          alt=""
          className="h-8 w-8 object-contain"
        />
        {compact ? null : (
          <span className="truncate text-lg font-semibold tracking-tight text-app-text">
            {branding.name}
          </span>
        )}
      </Link>
    </>
  );
}
function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useMe();
  const logout = useLogout();
  const user = data?.user;
  const displayName = user
    ? `${user.firstName} ${user.lastName}`.trim() ||
      user.email ||
      user.loginName ||
      ""
    : "";
  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase() ||
      (user.email ?? user.loginName ?? "").charAt(0).toUpperCase()
    : "";
  return (
    <Popover.Root>
      <Popover.Trigger
        as={Button}
        type="button"
        variant="text"
        aria-label={t("userMenu.label")}
        className="flex w-full items-center gap-3 text-left"
      >
        <Avatar.Root shape="circle">
          <Avatar.Fallback>{initials}</Avatar.Fallback>
        </Avatar.Root>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-app-text">
            {displayName}
          </span>
          <span className="block truncate text-xs text-app-muted">
            {user?.email}
          </span>
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side="top" align="start" sideOffset={8}>
          <Popover.Popup className="min-w-64">
            <Popover.Content>
              <ThemeSelector />
              <Button
                type="button"
                variant="text"
                fluid
                disabled={logout.isPending}
                onClick={() =>
                  logout.mutate(undefined, {
                    onSettled: () =>
                      navigate("/login", {
                        replace: true,
                      }),
                  })
                }
                className="mt-3 justify-start"
              >
                <i className="pi pi-sign-out" />
                {t("userMenu.logout")}
              </Button>
            </Popover.Content>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
const NAV_LABEL_DEFAULTS: Record<string, string> = {
  "settings.company": "Company",
  "settings.billing": "Billing",
  "settings.documents": "Documents",
  "settings.localization": "Localization",
  "settings.holidays": "Company holidays",
  "settings.categories": "Categories",
  "settings.roles": "Roles",
  "settings.aiTab": "AI",
  "settings.system": "System",
  "settings.systemAdmin": "System admin",
};

function NavigationTree({
  items,
  navLinkClass,
  onNavigate,
}: {
  items: NavItem[];
  navLinkClass: (state: { isActive: boolean }) => string;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const { hasAny } = usePermissions();
  const location = useLocation();
  const current = `${location.pathname}${location.search}`;
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const label = (key: string) =>
    t(key, { defaultValue: NAV_LABEL_DEFAULTS[key] ?? key });
  return (
    <nav className="flex min-h-0 flex-col gap-1">
      {items.map((item) => {
        const children = (item.children ?? []).filter(
          (child) =>
            (child.anyOf.length === 0 || hasAny(child.anyOf)) &&
            (child.allOf?.every((permission) => hasAny([permission])) ?? true),
        );
        if (children.length === 0) {
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => onNavigate?.()}
              className={navLinkClass}
            >
              <i className={cx(item.icon, "shrink-0 text-lg")} aria-hidden />
              {label(item.labelKey)}
            </NavLink>
          );
        }
        const childActive = children.some((child) =>
          child.to.includes("?")
            ? current === child.to
            : location.pathname.startsWith(child.to),
        );
        const open = toggled[item.to] ?? childActive;
        return (
          <div key={item.to}>
            <button
              type="button"
              aria-expanded={open}
              onClick={() =>
                setToggled((state) => ({ ...state, [item.to]: !open }))
              }
              className={navLinkClass({ isActive: false })}
            >
              <i className={cx(item.icon, "shrink-0 text-lg")} aria-hidden />
              {label(item.labelKey)}
              <i
                aria-hidden
                className={cx(
                  "pi pi-chevron-down ml-auto text-xs transition-transform",
                  open && "rotate-180",
                )}
              />
            </button>
            {open ? (
              <div className="ml-4 mt-1 flex flex-col gap-0.5 border-l border-app-border pl-2">
                {children.map((child) => {
                  const active = child.to.includes("?")
                    ? current === child.to
                    : location.pathname.startsWith(child.to);
                  return (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      onClick={() => onNavigate?.()}
                      className={() => navLinkClass({ isActive: active })}
                    >
                      <i
                        className={cx(child.icon, "shrink-0 text-sm")}
                        aria-hidden
                      />
                      {label(child.labelKey)}
                    </NavLink>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { has, hasAny } = usePermissions();
  const me = useMe();
  const [moreOpen, setMoreOpen] = useState(false);
  const exitImpersonation = useMutation({
    mutationFn: () => apiPost("/system-admin/exit-impersonation"),
    onSuccess: async () => {
      await me.refetch();
    },
  });
  const visible = NAV_ITEMS.filter(
    (item) =>
      (item.anyOf.length === 0 || hasAny(item.anyOf)) &&
      (item.allOf?.every((permission) => has(permission)) ?? true),
  ).map((item) =>
    item.to === "/employees" && !has("employees.view_all")
      ? { ...item, labelKey: "employees.myData" }
      : item,
  );
  const canCreateRequest = hasAny(["requests.create"]);
  const byTo = new Map(visible.map((item) => [item.to, item]));
  // Bottom bar: the most-used areas the user can actually open.
  const mobileMain = ["/", "/requests", "/time", "/offers", "/customers"]
    .map((to) => byTo.get(to))
    .filter((item): item is NavItem => item !== undefined)
    .slice(0, 3);
  const mobileMore = visible.filter((item) => !mobileMain.includes(item));
  const navLinkClass = ({ isActive }: { isActive: boolean }): string =>
    cx(
      "flex flex-row items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors w-full",
      isActive
        ? "bg-[var(--p-content-hover-background)] text-app-text"
        : "text-app-muted hover:bg-[var(--p-content-hover-background)] hover:text-app-text",
    );
  return (
    <div className="min-h-dvh bg-app-background">
      <LocaleSync />

      {/* Desktop sidebar */}
      <div className="hidden lg:flex">
        <aside className="fixed inset-y-0 left-0 z-20 flex h-dvh w-64 flex-col border-r border-app-border bg-app-surface">
          <div className="mx-3 flex h-16 shrink-0 items-center border-b border-app-border">
            <Logo />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            <NavigationTree items={visible} navLinkClass={navLinkClass} />
          </div>
          <div className="flex w-full shrink-0 border-t border-app-border p-3">
            <UserMenu />
          </div>
        </aside>
      </div>

      {/* Content */}
      <main className="min-h-[calc(100dvh-4rem)] px-4 pb-24 pt-4 sm:px-6 lg:pb-8 lg:pl-72 lg:pr-8 lg:pt-6">
        <div className="w-full">
          {me.data?.impersonating ? (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning-border bg-warning-soft px-4 py-3 text-sm text-app-text">
              <span>
                Impersonating an employee as{" "}
                {me.data.impersonating.actorLoginName ?? "system admin"}.
              </span>
              <Button
                type="button"
                size="small"
                variant="outlined"
                severity="secondary"
                loading={exitImpersonation.isPending || undefined}
                onClick={() => exitImpersonation.mutate()}
              >
                Exit impersonation
              </Button>
            </div>
          ) : null}
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom navigation */}
      <nav
        aria-label={t("nav.main")}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-app-border bg-app-surface pb-[env(safe-area-inset-bottom)] lg:hidden"
      >
        <div className="grid h-16 grid-cols-5 items-stretch">
          {mobileMain.slice(0, 2).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }: { isActive: boolean }) =>
                cx(
                  "flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium",
                  isActive ? "font-semibold text-app-text" : "text-app-muted",
                )
              }
            >
              <i className={cx(item.icon, "text-xl")} />
              {t(item.labelKey, {
                defaultValue:
                  NAV_LABEL_DEFAULTS[item.labelKey] ?? item.labelKey,
              })}
            </NavLink>
          ))}
          <div className="flex flex-col items-center justify-center">
            <Button
              type="button"
              iconOnly
              rounded
              raised
              aria-label={t("nav.newRequest")}
              disabled={!canCreateRequest}
              onClick={() => navigate("/requests/new")}
              className="h-14 w-14"
            >
              <i className="pi pi-plus text-xl" />
            </Button>
            <span className="mt-1 text-center text-[11px] font-medium text-app-muted">
              {t("nav.newRequest")}
            </span>
          </div>
          {mobileMain.slice(2).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }: { isActive: boolean }) =>
                cx(
                  "flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium",
                  isActive ? "font-semibold text-app-text" : "text-app-muted",
                )
              }
            >
              <i className={cx(item.icon, "text-xl")} />
              {t(item.labelKey)}
            </NavLink>
          ))}
          <Button
            type="button"
            variant="text"
            severity="secondary"
            onClick={() => setMoreOpen(true)}
            className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium"
          >
            <i className="pi pi-ellipsis-h text-xl" />
            {t("nav.more")}
          </Button>
        </div>
      </nav>

      {/* Mobile "More" sheet */}
      <Modal.Root
        open={moreOpen}
        onOpenChange={(event: { value: boolean | undefined }) =>
          !event.value && (() => setMoreOpen(false))()
        }
      >
        <Modal.Portal>
          <Modal.Backdrop></Modal.Backdrop>
          <Modal.Positioner>
            <Modal.Popup>
              <Modal.Header>
                <Modal.Title>{t("nav.more")}</Modal.Title>
                <Modal.HeaderActions>
                  <Modal.Close className="pi pi-times"></Modal.Close>
                </Modal.HeaderActions>
              </Modal.Header>
              <Modal.Content>
                <nav className="space-y-1" aria-label={t("nav.more")}>
                  {mobileMore.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={() => setMoreOpen(false)}
                      className={navLinkClass}
                    >
                      <i className={cx(item.icon, "shrink-0 text-lg")} />
                      {t(item.labelKey)}
                    </NavLink>
                  ))}
                </nav>
                <div className="mt-4 border-t border-app-border pt-4 w-64">
                  <UserMenu />
                </div>
              </Modal.Content>
            </Modal.Popup>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </div>
  );
}
